import { Component, HostListener } from '@angular/core';
import labels from './../data/transaction.json'
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SynagogueService } from '../services/synagogue.service';
import { SnackBarService } from '../../../services/snack-bar.service';
import { BalanceService } from '../services/balance.service';
import { amountToHebrewWords } from '../../../../../../shared/hebrewWords';
import { addMoney, coerceMoney, subtractMoney, toAgorot } from '../../../../../../shared/money';
import { ITransaction, MAX_TRANSACTION_AMOUNT } from '../../../../../../shared/schemas/synagogueSchema';

/** Snapshot of the form, frozen when the confirmation dialog opens. */
type PendingTransaction = {
  actionType: 1 | 2;
  amountPaid: number;
  amountWords: string;
  paymentType: string;
  paidTo: string;
  expenseType: string;
  checkNo: string;
};

/** Amounts from here up get an extra "check the amount" warning. */
const LARGE_AMOUNT = 50_000;

@Component({
  selector: 'app-synagogue-add-transaction',
  templateUrl: './synagogue-add-transaction.component.html',
  styleUrl: './synagogue-add-transaction.component.scss'
})
export class SynagogueAddTransactionComponent {

  constructor(private synService: SynagogueService,
              private balanceService: BalanceService,
              private snackBar: SnackBarService
  ){}
  header: string = labels.header
  loading: boolean = false
  addText: string = labels.add
  addTransactionForm: FormGroup = new FormGroup({
    actionType: new FormControl('', [
      Validators.required,
      Validators.min(1),
      Validators.max(2),
    ]),
    paymentType: new FormControl('', [
      Validators.required,
      Validators.maxLength(10),
      Validators.minLength(1),
    ]),
    expenseType: new FormControl('', []),
    checkNo: new FormControl('', []),
    amountPaid: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0.01),
      Validators.max(MAX_TRANSACTION_AMOUNT),
    ]),
    paidTo: new FormControl('', [
      Validators.required,
      Validators.maxLength(30),
      Validators.minLength(1),
    ]),
  })

  /** Non-null while the confirmation dialog is open. */
  confirming: PendingTransaction | null = null
  /** Synagogue balance right now — loaded when the dialog opens. */
  currentBalance: number | null = null
  balanceLoading = false

  /** Step 1: freeze the form into a snapshot and ask for confirmation. Nothing is saved yet. */
  openConfirm(): void {
    if (this.addTransactionForm.invalid || this.loading) {
      this.addTransactionForm.markAllAsTouched()
      return
    }
    const v = this.addTransactionForm.value
    const amount = Number(v.amountPaid)
    const actionType: 1 | 2 = Number(v.actionType) === 2 ? 2 : 1
    this.confirming = {
      actionType,
      amountPaid: amount,
      amountWords: amountToHebrewWords(amount),
      paymentType: v.paymentType ?? '',
      paidTo: v.paidTo ?? '',
      expenseType: actionType === 2 ? (v.expenseType ?? '') : '',
      checkNo: v.paymentType === 'צק' ? (v.checkNo ?? '') : '',
    }
    this.loadCurrentBalance()
  }

  private async loadCurrentBalance(): Promise<void> {
    this.currentBalance = null
    this.balanceLoading = true
    try {
      const latest = await this.balanceService.refreshLatest()
      this.currentBalance = latest?.balance ?? 0
    } catch (err) {
      console.error('Balance load for confirmation failed:', err)
    } finally {
      this.balanceLoading = false
    }
  }

  /** Balance after this transaction, computed in exact agorot. */
  get balanceAfter(): number | null {
    if (!this.confirming || this.currentBalance === null) return null
    const current = coerceMoney(this.currentBalance)
    const amount = coerceMoney(this.confirming.amountPaid)
    return this.confirming.actionType === 1
      ? addMoney(current, amount)
      : subtractMoney(current, amount)
  }

  get isLargeAmount(): boolean {
    return (this.confirming?.amountPaid ?? 0) >= LARGE_AMOUNT
  }

  /** "₪1,234" for whole amounts, "₪1,234.50" with agorot. */
  formatAmount(n: number): string {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: toAgorot(Math.abs(n)) % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(n)
  }

  @HostListener('document:keydown.escape')
  cancelConfirm(): void {
    if (!this.loading) this.confirming = null
  }

  /** Step 2: the admin confirmed — save exactly the snapshot they saw. */
  async confirmSubmit(): Promise<void> {
    const p = this.confirming
    if (!p || this.loading) return
    this.loading = true
    const body: ITransaction = {
      actionType: p.actionType,
      amountPaid: p.amountPaid,
      paymentType: p.paymentType,
      expenseType: p.expenseType,
      checkNo: p.checkNo,
      paidTo: p.paidTo,
    }
    try {
      const res = await this.synService.addTransaction(body)
      this.confirming = null
      this.snackBar.openToast('הפעולה נשמרה בהצלחה', {
        kind: 'success',
        description: `${p.actionType === 1 ? 'הכנסה' : 'הוצאה'} של ${this.formatAmount(p.amountPaid)}. יתרת בית הכנסת עכשיו: ${this.formatAmount(res.balance)}`,
      })
      this.addTransactionForm.reset({
        actionType: '', paymentType: '', expenseType: '', checkNo: '', amountPaid: null, paidTo: '',
      })
      // Resync the store so the dashboard total includes this transaction.
      this.balanceService.refreshLatest().catch((err) =>
        console.error('Balance refresh after add failed:', err))
    } catch (error) {
      console.error(error)
      this.snackBar.openToast('הפעולה לא נשמרה', {
        kind: 'error',
        description: 'לא בוצע שום שינוי ביתרה. בדקו את החיבור ונסו שוב.',
      })
    } finally {
      this.loading = false
    }
  }
}
